import React from 'react';
import { ToastContainer } from 'react-toastify';
import IncomeSection from './IncomeSection';
import ExpensesSection from './ExpensesSection';
import V3FinancialSummarySection from './V3FinancialSummarySection';
import EventDetails from '../../EventDetails/components/EventDetails';
import CurrencyConversionToggle from './CurrencyConversionToggle';
import V3OfferEventHeader from './V3OfferEventHeader';
import V3OfferSeatMapPicker from './V3OfferSeatMapPicker';
import EventTemplateBar from './EventTemplateBar';
import MetricColumn from './MetricColumn';
import V3OfferLineupRail from './V3OfferLineupRail';
import V3OfferDealTermsPane from './V3OfferDealTermsPane';
import V3OfferEditModals from './V3OfferEditModals';
import useV3OfferEditController from '../hooks/useV3OfferEditController';
import { canCreateConfirm } from '../../../shared/offerConfirmEligibility';


const V3FinancePage = () => {
  const ctrl = useV3OfferEditController();
  const {
    csrfToken,
    team,
    currentUser,
    offer,
    budget,
    lineItems,
    eventOffers,
    confirm,
    performers,
    handlePreview,
    handleCopy,
    handleCopyHolds,
    canCopyHolds,
    handleDownload,
    handleDelete,
    handleChangeActType,
    updateOffer,
    handlePassThroughChange,
    reorderLineItem,
    budgetForComponents,
    siblingGuaranteeExpenses,
    budgetForMath,
    calculated,
    eventVenueNet,
    hasSecondaryCurrencySymbol,
    globalCurrency,
    showCurrencyConversion,
    handleUpdateBudget,
    handleAutoSaveLineItem,
    handleDeleteLineItem,
    handleCreateTicketLineItem,
    handleCreateIncomeLineItem,
    handleCreateAdditionLineItem,
    handleCreateExpenseLineItem,
    handleCreateDeductionLineItem,
    handleCreateBeforeAdjustedDeductionsLineItem,
    handleCreateAncillaryIncomeLineItem,
    handleCreateAncillaryExpenseLineItem,
    formatCalculatedNumber,
    poolActive,
    artistEarningsOffer,
    artistEarningsForecast,
    artistEarningsActual,
    handleSendOffer,
    handleSelectOffer,
    handleReorder,
    openAddArtist,
    offerActive,
    forecastActive,
    actualsActive,
    handleColumnToggle,
    handleVisibilityChange,
    handleReorderLineItems,
    bonusManagerProps,
    isMobile,
    summaryExpanded,
    setSummaryExpanded,
    utilityBarHeight,
    financeCategories,
    addFinanceCategory,
    offerContacts,
    setOfferContacts,
    handleAddContact,
    settlementTickets,
    addOnActuals,
    setLinkOfferModalOpen,
    setProFormaOpen,
    setHoldSheetOpen,
    setCreateConfirmOpen,
    openChangeArtist,
    handleConfirmSaved,
  } = ctrl;

  if (!offer || !budget) {
    return (
      <div className="text-center py-5">
        <i className="fas fa-spinner fa-spin mr-2"></i>
        Loading...
      </div>
    );
  }

  return (
    <div>
      <ToastContainer icon={false} />
      <V3OfferEventHeader
        offer={offer}
        confirm={confirm}
        team={team}
        offerActive={offerActive}
        forecastActive={forecastActive}
        actualsActive={actualsActive}
        onToggleColumn={handleColumnToggle}
        onLinkEvent={() => setLinkOfferModalOpen(true)}
        templateBar={
          <EventTemplateBar
            team={team}
            csrfToken={csrfToken}
            budget={budgetForComponents}
            eventTemplates={ctrl.eventTemplates}
            onApplyTemplate={ctrl.handleApplyEventTemplate}
            onTemplateCreated={ctrl.handleEventTemplateCreated}
            onTemplateSaved={ctrl.handleEventTemplateSaved}
            onBudgetPatched={ctrl.handleBudgetPatched}
          />
        }
      />
      {hasSecondaryCurrencySymbol && (
        <div style={{ marginBottom: '8px' }}>
          <CurrencyConversionToggle
            enabled={offer.display_currency_conversion_for_offer || false}
            onToggle={() => {
              updateOffer(offer.id, {
                display_currency_conversion_for_offer: !offer.display_currency_conversion_for_offer
              });
            }}
            label={team.secondary_currency_symbol}
            tooltip={`Display currency conversion\n1 ${team.base_currency_symbol} = ${team.currency_conversion_rate} ${team.secondary_currency_symbol}`}
          />
        </div>
      )}

      <V3OfferSeatMapPicker />

      {confirm ? (
        <EventDetails
          csrfToken={csrfToken}
          team={team}
          confirm={confirm}
          venueOwnerships={team.venue_ownerships}
          rooms={team.rooms}
          currentUser={currentUser}
          onConfirmSaved={handleConfirmSaved}
        />
      ) : null}

      <div className="offers-v3-edit-layout">
        {(offer.lineup || []).length > 0 && (
          <V3OfferLineupRail
            lineup={offer.lineup || []}
            activeOfferId={offer.id}
            onSelectOffer={handleSelectOffer}
            onAddArtist={openAddArtist}
            onReorder={handleReorder}
            team={team}
            offer={offer}
            poolActive={poolActive}
            poolMembers={calculated?.poolMembers || []}
          />
        )}
        <div className="offers-v3-edit-main">
          <V3OfferDealTermsPane
            team={team}
            offer={offer}
            budget={budgetForComponents}
            confirm={confirm}
            currentUser={currentUser}
            csrfToken={csrfToken}
            actType={offer?.primary_performer?.act_type || offer?.act_type}
            onPreview={handlePreview}
            onCopy={handleCopy}
            onCopyHolds={handleCopyHolds}
            canCopyHolds={canCopyHolds}
            onDownload={handleDownload}
            onCreateHold={() => setHoldSheetOpen(true)}
            onCreateConfirm={() => setCreateConfirmOpen(true)}
            canConfirm={canCreateConfirm(offer, currentUser)}
            onDelete={handleDelete}
            onSend={handleSendOffer}
            onAddContact={handleAddContact}
            onChangeActType={handleChangeActType}
            bonusManagerProps={bonusManagerProps}
            walkoutPotential={Number(offer?.walkout_potential) || 0}
            onEditPerformer={openChangeArtist}
            offerContacts={offerContacts}
            offerContactsChanged={setOfferContacts}
            contactsDefaultOpen={(offerContacts || []).some((oc) => oc.id === undefined)}
            calculated={calculated}
          />
        </div>
      </div>

      <div className="mt-3">
        <IncomeSection
          csrfToken={csrfToken}
          team={team}
          currentUser={currentUser}
          offer={offer}
          budget={budgetForComponents}
          calculated={calculated}
          updateBudget={handleUpdateBudget}
          onPassThroughChange={handlePassThroughChange}
          createTicketBudgetLineItem={handleCreateTicketLineItem}
          createIncomeBudgetLineItem={handleCreateIncomeLineItem}
          createAncillaryIncomeBudgetLineItem={handleCreateAncillaryIncomeLineItem}
          createBeforeAdjustedDeductionsBudgetLineItem={handleCreateBeforeAdjustedDeductionsLineItem}
          createAdditionBudgetLineItem={handleCreateAdditionLineItem}
          deleteBudgetLineItem={handleDeleteLineItem}
          autoSaveBudgetLineItem={handleAutoSaveLineItem}
          formatCalculatedNumber={formatCalculatedNumber}
          financeCategories={financeCategories}
          onFinanceCategoryAdded={addFinanceCategory}
          settlementTickets={settlementTickets}
          addOnActuals={addOnActuals}
          performers={performers}
          onVisibilityChange={handleVisibilityChange}
          showCurrencyConversion={showCurrencyConversion}
          globalCurrency={globalCurrency}
          reorderLineItem={reorderLineItem}
          onReorderLineItems={handleReorderLineItems}
          allLineItems={lineItems}
        />
      </div>

      <div style={{ marginTop: '25px' }}>
        <ExpensesSection
          csrfToken={csrfToken}
          team={team}
          currentUser={currentUser}
          offer={offer}
          budget={budgetForComponents}
          calculated={calculated}
          updateBudget={handleUpdateBudget}
          createExpensesBudgetLineItem={handleCreateExpenseLineItem}
          createDeductionBudgetLineItem={handleCreateDeductionLineItem}
          createAncillaryExpensesBudgetLineItem={handleCreateAncillaryExpenseLineItem}
          deleteBudgetLineItem={handleDeleteLineItem}
          autoSaveBudgetLineItem={handleAutoSaveLineItem}
          formatCalculatedNumber={formatCalculatedNumber}
          financeCategories={financeCategories}
          onFinanceCategoryAdded={addFinanceCategory}
          performers={performers}
          onVisibilityChange={handleVisibilityChange}

          showCurrencyConversion={showCurrencyConversion}
          globalCurrency={globalCurrency}
          reorderLineItem={reorderLineItem}
          onReorderLineItems={handleReorderLineItems}
          allLineItems={lineItems}
          siblingRows={siblingGuaranteeExpenses}
          onSelectSiblingOffer={handleSelectOffer}
        />
      </div>

      <div style={{ height: '16px' }} />

      <div className="v3-sticky-footer" style={{
        position: 'sticky',
        bottom: utilityBarHeight,
        zIndex: 10,
        backgroundColor: 'var(--od-c-ffffff, #ffffff)',
        border: '1px solid var(--od-c-d5d7da, #D5D7DA)',
        borderBottom: 'none',
        borderRadius: '8px 8px 0 0',
        boxShadow: '0 -2px 4px rgba(0, 0, 0, 0.05)',
        overflow: 'clip',
        padding: isMobile ? '12px 12px 0 12px' : '16px 16px 0 16px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <div style={{ flex: '1 0 0', display: 'flex', alignItems: 'flex-start', alignSelf: 'stretch' }}>
            <div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '4px' : '8px' }}>
                <span style={{
                  fontSize: isMobile ? '14px' : '16px',
                  fontWeight: 600,
                  lineHeight: isMobile ? '20px' : '24px',
                  color: 'var(--od-c-333333, #333333)'
                }}>
                  Financial Summary
                </span>
                <a
                  href="#"
                  onClick={(e) => { e.preventDefault(); setProFormaOpen(true); }}
                  style={{
                    fontSize: isMobile ? '13px' : '14px',
                    fontWeight: 600,
                    lineHeight: '20px',
                    color: 'var(--od-c-136395, #136395)',
                    textDecoration: 'none'
                  }}
                >
                  View Pro-Forma
                </a>
              </div>
            </div>
          </div>
          {!summaryExpanded && !isMobile && (
            <div style={{ flex: '1 0 0', display: 'flex', alignItems: 'center', alignSelf: 'stretch' }}>
              <div style={{ flex: '1 0 0', paddingLeft: '16px' }}>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', justifyContent: 'flex-end', padding: '0 80px 0 0' }}>
                  <MetricColumn
                    active={offerActive}
                    title="Offer"
                    artistEarningsLabel="Potential Artist Earnings"
                    artistEarningsValue={artistEarningsOffer}
                    venueNetLabel="Venue Net Potential"
                    venueNetTooltip="The venue's net across all artists and expenses on this event."
                    venueNetValue={eventVenueNet?.offer ?? calculated?.showSummary?.venueNetPotential}
                    showBorderRight={forecastActive || actualsActive}
                    formatCalculatedNumber={formatCalculatedNumber}
                  />
                  <MetricColumn
                    active={forecastActive}
                    title="Forecast"
                    artistEarningsLabel="Potential Artist Earnings"
                    artistEarningsValue={artistEarningsForecast}
                    venueNetLabel="Venue Net Potential"
                    venueNetTooltip="The venue's net across all artists and expenses on this event."
                    venueNetValue={eventVenueNet?.forecast ?? calculated?.showSummary?.venueNetPotentialForecast}
                    showBorderRight={actualsActive}
                    formatCalculatedNumber={formatCalculatedNumber}
                  />
                  <MetricColumn
                    active={actualsActive}
                    title="Actuals"
                    artistEarningsLabel="Artist Earnings"
                    artistEarningsValue={artistEarningsActual}
                    venueNetLabel="Venue Net"
                    venueNetTooltip="The venue's net across all artists and expenses on this event."
                    venueNetValue={eventVenueNet?.actual ?? calculated?.showSummary?.venueNetPotentialActual}
                    showBorderRight={false}
                    formatCalculatedNumber={formatCalculatedNumber}
                  />
                </div>
              </div>
            </div>
          )}
          <button
            onClick={() => setSummaryExpanded(!summaryExpanded)}
            style={{
              position: 'absolute',
              right: isMobile ? '12px' : '16px',
              top: isMobile ? '18px' : '31px',
              width: '28px',
              height: '28px',
              padding: 0,
              borderRadius: '9999px',
              backgroundColor: 'var(--od-c-fafafa, #fafafa)',
              border: '1px solid var(--od-c-d5d7da, #D5D7DA)',
              boxShadow: '0px 1px 2px rgba(10, 13, 18, 0.05)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              outline: 'none'
            }}
          >
            <i className={`far ${summaryExpanded ? 'fa-chevron-down' : 'fa-chevron-right'}`} style={{ fontSize: '12px', color: 'var(--od-c-535862, #535862)' }} />
          </button>
        </div>
        {summaryExpanded && (
          <div style={{ padding: isMobile ? '12px 4px' : '32px' }}>
            <div className="form-row">
              <V3FinancialSummarySection
                calculated={calculated}
                team={team}
                offer={offer}
                budget={budgetForMath}
                eventVenueNet={eventVenueNet}
                showCurrencyConversion={showCurrencyConversion}
                globalCurrency={globalCurrency}
                eventOffers={eventOffers?.length > 0 ? eventOffers : [offer]}
                embedded={true}
              />
            </div>
          </div>
        )}
      </div>
      <V3OfferEditModals ctrl={ctrl} />
    </div>
  );
};

export default V3FinancePage;
